import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Pencil, Eye, EyeOff, Inbox } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useAdminRoles,
  useAdminApplications,
  useUpsertRole,
  useDeleteRole,
  type RoleRow,
} from '@/lib/admin-queries';
import { RoleEditForm } from '@/features/careers/RoleEditForm';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/Toast';

const EMPTY: RoleRow = {
  slug: '',
  title: '',
  department: 'Engineering',
  location: 'Remote',
  type: 'full-time',
  description_md: '',
  responsibilities: [],
  requirements: [],
  nice_to_haves: [],
  is_open: true,
  apply_email: 'careers@vonlof.co',
  status_publish: 'draft',
  published_at: null,
};

export function CareersPage() {
  const { data: rows = [], isLoading } = useAdminRoles();
  // Pull every application once so we can show counts per role without
  // querying per row.
  const { data: apps = [] } = useAdminApplications();
  const upsert = useUpsertRole();
  const remove = useDeleteRole();
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [deleting, setDeleting] = useState<RoleRow | null>(null);

  const appsBySlug = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of apps) {
      if (!a.role_slug) continue;
      m.set(a.role_slug, (m.get(a.role_slug) ?? 0) + 1);
    }
    return m;
  }, [apps]);

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Role',
        cell: (r: RoleRow) => (
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-xs text-fg-subtle">{r.slug}</p>
          </div>
        ),
      },
      {
        key: 'department',
        header: 'Department',
        cell: (r: RoleRow) => <Badge variant="gold">{r.department}</Badge>,
      },
      {
        key: 'type',
        header: 'Type',
        cell: (r: RoleRow) => <Badge variant="outline">{r.type}</Badge>,
      },
      {
        key: 'is_open',
        header: 'Status',
        cell: (r: RoleRow) =>
          r.is_open ? <Badge variant="success">Open</Badge> : <Badge>Closed</Badge>,
      },
      {
        key: 'published',
        header: 'Posted',
        cell: (r: RoleRow) => <span className="text-xs text-fg-muted">{r.published_at ? formatDate(r.published_at) : '—'}</span>,
      },
      {
        key: 'applications',
        header: 'Applications',
        cell: (r: RoleRow) => {
          const n = appsBySlug.get(r.slug) ?? 0;
          return n === 0 ? (
            <span className="text-xs text-fg-subtle">—</span>
          ) : (
            <Link
              to={`/applications?role=${encodeURIComponent(r.slug)}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-full border border-gold-500/30 bg-gold-500/10 px-2 py-0.5 text-xs font-medium text-gold-500 hover:bg-gold-500/15"
            >
              <Inbox className="h-3 w-3" /> {n}
            </Link>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: RoleRow) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label={r.is_open ? 'Close role' : 'Open role'}
              onClick={async (e) => {
                e.stopPropagation();
                if (!r.id) return;
                try {
                  await upsert.mutateAsync({ ...r, is_open: !r.is_open });
                  toast.success(r.is_open ? 'Role closed' : 'Role reopened');
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              {r.is_open ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            {r.status_publish === 'published' && (
              <a
                href={`https://vonlof.co/careers/${r.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View live"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [upsert, appsBySlug],
  );

  async function onSave(row: RoleRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Role updated' : 'Role created');
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Role deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Careers</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Roles</h1>
          <p className="mt-2 text-sm text-fg-muted">Manage open roles and triage applications.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({ ...EMPTY })}>
          New role
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No roles yet. Click New role to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Edit role' : 'New role'}
        size="xl"
      >
        {editing && (
          <RoleEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this role?"
        description={deleting ? `"${deleting.title}" will be removed and applications will no longer be accepted.` : ''}
        confirmLabel="Delete role"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}